/**
 * Чистая арифметика хронологии и сценария (спека §9.2, v2 §1.3, §1.6): ширина блоков, ±0,5 с, итог
 * хронологии и секунды без единицы (единица — вне моноширинного числа), автоподбор референсов, потолок
 * длительности кадра по моделям, разметка `_fixes`, строки титра (только старые версии и монтажи), сценарий v1 → форма v2.
 *
 * Без импортов из `@/…`: модуль проверяется `node --test` (tests/videoTimeline.test.ts) как есть.
 * Правила — копии серверных: `project.DURATION_MS`/`DURATION_STEP_MS`, `videoscenario.auto_refs`,
 * `videoscenario.max_duration`, `videoscenario.validate_scenario` (`_fixes`, сумма за вычетом наплывов,
 * лицо в финале со спины), `videoscenario.title_lines`.
 */

/** Поле `shot_duration_ms`: 1000..15000, кратно 500 (`project.DURATION_MS`). */
export const DURATION_MIN_MS = 1000;
export const DURATION_MAX_MS = 15000;
export const DURATION_STEP_MS = 500;
/** Блок хронологии уже этого не читается — номер и кнопки не влезут. */
export const BLOCK_MIN_PX = 72;
/** Допуск суммы сценария против цели (`SUM_TOLERANCE_S`): вкладка «Сценарий», не хронология (№310). */
export const SUM_TOLERANCE_MS = 500;
/** Референсов в запросе композера (`COMPOSER_REFS`). */
export const COMPOSER_REFS = 6;
/** Потолок кадра без выбора моделей и у ручной версии (`MAX_SHOT_S`). */
export const MAX_SHOT_S = 15;

/**
 * Секунды без единицы: «15», «14,5». Единицу «с» ставят ВНЕ `.tnum`: у него моноширинный шрифт, и
 * пробел внутри него шириной в цифру — «15  с» читался как двойной пробел (v2 §0).
 */
export function secNum(ms: number): string {
  return String(Math.round(ms / 100) / 10).replace(".", ",");
}

/**
 * Итог хронологии без единицы: «15,0» — сумма длительностей кадров, всегда с десятыми.
 *
 * Цели ролика здесь больше нет (задача Оли №310, 15.10): у проекта «из кадра» её никто не вводил —
 * она производная от длительности куска, а длительности кадров сотрудник правит руками, и «10,0 / 8»
 * сообщало о расхождении с величиной, которой он не управляет. Цель осталась там, где она план:
 * подзаголовок проекта «из платья» и вкладка «Сценарий» (там её держит и сервер — `SUM_TOLERANCE_S`).
 */
export function totalText(sumMs: number): string {
  return (Math.round(sumMs / 100) / 10).toFixed(1).replace(".", ",");
}

/** Длительность после «−0,5 / +0,5»; `null` — шаг выводит за границы (или длительности нет). */
export function nudge(ms: number | null, deltaMs: number): number | null {
  if (ms === null) return null;
  const next = ms + deltaMs;
  return next < DURATION_MIN_MS || next > DURATION_MAX_MS ? null : next;
}

/**
 * Ширины блоков ∝ длительности: дорожка `trackPx` соответствует СУММЕ длительностей, но блок не уже
 * `BLOCK_MIN_PX`. Кадр без длительности — минимальный блок; все кадры без длительности (сумма 0) —
 * минимальные блоки, а не деление на ноль.
 *
 * Масштаб считался от большего из цели и суммы, пока цель была на экране (№310): дорожка короче цели
 * оставляла пустой хвост, который читался как «не хватает ролика». Теперь дорожка — это сами кадры.
 */
export function blockWidths(durations: readonly (number | null)[], trackPx: number): number[] {
  const sum = durations.reduce<number>((n, d) => n + (d ?? 0), 0);
  const scale = trackPx / Math.max(sum, 1);
  return durations.map((d) => Math.max(BLOCK_MIN_PX, Math.round((d ?? 0) * scale)));
}

/** Итог хронологии: сумма длительностей кадров. Против цели он больше не сверяется — см. `totalText`. */
export function totalOf(durations: readonly (number | null)[]): { sumMs: number } {
  return { sumMs: durations.reduce<number>((n, d) => n + (d ?? 0), 0) };
}

/** Куда встанет перетаскиваемый блок: перед первым, чья середина правее курсора. */
export function insertIndex(mids: readonly number[], x: number): number {
  const n = mids.findIndex((mid) => x < mid);
  return n < 0 ? mids.length : n;
}

/** Порядок с `id`, поставленным на место `index` среди остальных. */
export function moveTo(order: readonly number[], id: number, index: number): number[] {
  const rest = order.filter((x) => x !== id);
  const at = Math.max(0, Math.min(index, rest.length));
  return [...rest.slice(0, at), id, ...rest.slice(at)];
}

/** Автоподбор композера (`auto_refs`): вердикт works первыми, внутри групп — порядок библиотеки. */
export function autoRefIds(items: readonly { id: number; verdict: string | null }[], n = COMPOSER_REFS): number[] {
  const works = items.filter((i) => i.verdict === "works");
  const rest = items.filter((i) => i.verdict !== "works");
  return [...works, ...rest].slice(0, n).map((i) => i.id);
}

export type DurationModel = {
  code: string;
  tier: string | null;
  fallback_for: string | null;
  durations: readonly number[];
};

/** Модели первого прогона: с уровнем и не запасные (`max_duration` без выбора). */
export function firstRunCodes(models: readonly DurationModel[]): string[] {
  return models.filter((m) => m.tier !== null && !m.fallback_for).map((m) => m.code);
}

/**
 * Потолок кадра, с. `min(max(durations))` по выбранным моделям (нет выбора — модели первого прогона);
 * модели без discovery (пустые `durations`) не ограничивают. Не выше `MAX_SHOT_S`.
 */
export function maxShotSeconds(models: readonly DurationModel[], chosen: readonly string[]): number {
  const codes = new Set(chosen.length ? chosen : firstRunCodes(models));
  const tops = models.filter((m) => codes.has(m.code) && m.durations.length).map((m) => Math.max(...m.durations));
  return tops.length ? Math.min(MAX_SHOT_S, ...tops) : MAX_SHOT_S;
}

/** Что сервер дописал за композера (`_fixes`): негатив и номера кадров (с 1) по виду правки. */
export type FixMarks = { negative: boolean; invariant: Set<number>; heroine: Set<number>; other: string[] };

export function fixMarks(fixes: readonly string[] | undefined): FixMarks {
  const out: FixMarks = { negative: false, invariant: new Set(), heroine: new Set(), other: [] };
  for (const fix of fixes ?? []) {
    const shot = /^(invariant|heroine): shot (\d+)$/.exec(fix);
    if (shot) out[shot[1] === "invariant" ? "invariant" : "heroine"].add(Number(shot[2]));
    else if (fix.startsWith("negative")) out.negative = true;
    else out.other.push(fix);
  }
  return out;
}

/** Длительности пустой ручной версии: цель на `n` кадров, кратно 0,5 с, остаток — последнему. */
export function splitTarget(targetMs: number, n: number): number[] {
  const each = Math.floor(targetMs / n / DURATION_STEP_MS) * DURATION_STEP_MS;
  return Array.from({ length: n }, (_, i) => (i === n - 1 ? targetMs - each * (n - 1) : each) / 1000);
}

// ---------------------------------------------------------------------------
// сценарий v2 (спека v2 §1.2–1.3)
// ---------------------------------------------------------------------------

/** Наплыв съедает от суммы кадров (`videoscenario.CROSSFADE_S`). */
export const CROSSFADE_S = 0.3;
/** Финал этой крупности — всегда без лица (`FACELESS_FINALE`). */
export const FACELESS_FINALE = ["back", "detail"];
/** Лимиты v2 (`TOP_LIMITS`, `DETAILS_RANGE`, `DETAIL_LIMIT`). */
export const DETAILS_MAX = 6;
export const DETAIL_MAX = 80;

/** Сумма кадров сценария, с: наплыв после кадра (кроме последнего) отнимает 0,3 с. */
export function scenarioSumS(shots: readonly { duration_s: number; transition?: string | null }[]): number {
  const fades = shots.slice(0, -1).filter((s) => s.transition === "crossfade").length;
  const sum = shots.reduce((n, s) => n + (Number(s.duration_s) || 0), 0) - CROSSFADE_S * fades;
  return Math.round(sum * 10) / 10;
}

type ShotSource = {
  title: string;
  duration_s: number;
  framing: string;
  camera: string;
  action: string;
  dress_focus: string;
  keyframe_prompt: string;
  video_prompt: string;
  transition: string;
  face_visible?: boolean | undefined;
};

type ScenarioSource = {
  title_name?: string | undefined;
  title_cta?: string | undefined;
  title_text?: string | undefined;
  logline: string;
  hook: string;
  negative: string;
  heroine: string;
  light: string;
  dress_details?: readonly string[] | undefined;
  shots: readonly ShotSource[];
};

/** Лицо в кадре: из версии; у v1 его нет — финал со спины/деталью без лица, остальные с лицом. */
export function faceVisible(shot: ShotSource, n: number, count: number): boolean {
  if (typeof shot.face_visible === "boolean") return shot.face_visible;
  return !(n === count - 1 && FACELESS_FINALE.includes(shot.framing));
}

/**
 * Детали платья версии; у v1 их нет — «акценты платья» кадров без повторов (до 6, ≤ 80 знаков):
 * правка и копия версии v1 уходят формой v2, а не 422.
 */
export function dressDetails(json: ScenarioSource): string[] {
  if (json.dress_details && json.dress_details.length) return [...json.dress_details];
  const seen = new Set<string>();
  for (const s of json.shots) {
    const text = s.dress_focus.split(/\s+/).filter(Boolean).join(" ").slice(0, DETAIL_MAX).trim();
    if (text) seen.add(text);
  }
  return [...seen].slice(0, DETAILS_MAX);
}

/** Версия в форме v2 для `scenario/manual`: без титра, звука и `_fixes` (у сценария их больше нет). */
export type ScenarioDraftV2<S extends ShotSource = ShotSource> = {
  logline: string;
  hook: string;
  negative: string;
  heroine: string;
  light: string;
  dress_details: string[];
  shots: (S & { face_visible: boolean })[];
};

/** Любая версия (v1 или v2) → форма v2: детали платья, лицо у каждого кадра; титра и звука в форме нет. */
export function toV2<S extends ShotSource>(json: ScenarioSource & { shots: readonly S[] }): ScenarioDraftV2<S> {
  return {
    logline: json.logline,
    hook: json.hook,
    negative: json.negative,
    heroine: json.heroine,
    light: json.light,
    dress_details: dressDetails(json),
    shots: (json.shots as readonly S[]).map((s, n): S & { face_visible: boolean } => ({
      ...s,
      face_visible: faceVisible(s, n, json.shots.length),
    })),
  };
}

/** Чего не хватает форме v2 до сохранения (сервер ответил бы 422): детали платья. */
export function v2Gaps(d: { dress_details: readonly string[] }): string[] {
  const out: string[] = [];
  const details = d.dress_details.filter((x) => x.trim());
  if (details.length < 1 || details.length > DETAILS_MAX) out.push(`детали платья: 1–${DETAILS_MAX} строк`);
  if (details.some((x) => x.trim().length > DETAIL_MAX)) out.push(`деталь платья: не длиннее ${DETAIL_MAX} знаков`);
  return out;
}
